import { useCallback, useMemo, useRef, useState } from 'react'

/**
 * Transport-free lifecycle shared by governed forms. It returns state, never
 * JSX, so hosts retain their geometry, copy, authority checks and write doors.
 * Dirty means different (not merely touched), refusals keep edits, and only
 * dirty keys travel.
 */

export type FormTransactionPhase =
  | 'loading' | 'idle' | 'dirty' | 'saving' | 'saved' | 'invalid' | 'refused'

export interface FormTransactionOptions {
  /** true while the persisted values are still being read */
  loading?: boolean
  /** the persisted value per key, as the server reports it */
  values: Readonly<Record<string, unknown>>
  /**
   * Client-side validation over the draft; one message per FAILING key.
   *
   * The result is sparse by design — a validator says what is wrong, not what
   * is fine — so the value type admits `undefined`. Without that, the ordinary
   * inline shape `cond ? {} : { key: 'message' }` widens to a union TypeScript
   * will not assign to `Record<string, string>`, and every caller has to
   * annotate its own arrow to say something the signature should have said.
   */
  validate?: (draft: Readonly<Record<string, unknown>>) => Readonly<Record<string, string | undefined>>
  /**
   * Persist the dirty keys. A REJECTION marks the form `refused` and is handed
   * back verbatim as `saveError` — never re-dressed as a validation error on a
   * field, and never classified here. Whether the door was closed, the network
   * was out or the code was wrong is a transport judgement the HOST makes
   * (`classifyWrite` and `WriteFailureNotice` live in the app), which is the
   * same seam `StateBoundary` states.
   */
  onSave: (patch: Readonly<Record<string, unknown>>) => void | Promise<void>
}

export interface FormTransactionState {
  phase: FormTransactionPhase
  /** what a control should render: the edit if there is one, else the persisted value */
  value: (key: string) => unknown
  set: (key: string, next: unknown) => void
  /** the keys whose draft differs from the persisted value */
  dirtyKeys: readonly string[]
  isDirty: boolean
  /** exactly what `save()` would send */
  patch: Readonly<Record<string, unknown>>
  /** validation messages a reader should currently SEE (edited keys, or all after a blocked save) */
  errors: Readonly<Record<string, string>>
  /** the rejection from the last save, verbatim and unclassified */
  saveError: unknown
  save: () => Promise<void>
  reset: () => void
}

/**
 * Values are compared by identity first and by their JSON shape second. A
 * value may be an object or an array, and
 * `Object.is` on two structurally identical objects is false — which would make
 * a form permanently dirty the moment a json setting was re-read.
 */
export function sameFormValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false
  try { return JSON.stringify(a) === JSON.stringify(b) } catch { return false }
}

export function useFormTransaction({ loading = false, values, validate, onSave }: FormTransactionOptions): FormTransactionState {
  const [edits, setEdits] = useState<Record<string, unknown>>({})
  const [saving, setSaving] = useState(false)
  const [outcome, setOutcome] = useState<{ kind: 'saved' } | { kind: 'refused'; error: unknown } | null>(null)
  /* Set when a save is BLOCKED by validation. Until then a message is shown
     only for a key the reader has actually edited — the same restraint the
     `Form` primitive shows with `touched`/`tried`, for the same reason: a page
     that opens shouting about values nobody has touched teaches people to
     ignore it. */
  const [tried, setTried] = useState(false)
  /* `values` is a fresh object on every render for most callers, so the draft
     memo would recompute constantly. The ref keeps `save()` reading the current
     values without making the callback identity churn. */
  const latest = useRef({ values, edits })
  latest.current = { values, edits }

  const value = useCallback(
    (key: string) => (key in latest.current.edits ? latest.current.edits[key] : latest.current.values[key]),
    [],
  )

  const set = useCallback((key: string, next: unknown) => {
    // A new edit ends the previous outcome: `saved` is a statement about a form
    // nobody has touched since, and a refusal is about a patch that no longer
    // exists once its contents change.
    setOutcome(null)
    setEdits((current) => ({ ...current, [key]: next }))
  }, [])

  const reset = useCallback(() => { setEdits({}); setOutcome(null); setTried(false) }, [])

  const dirtyKeys = useMemo(
    () => Object.keys(edits).filter((key) => !sameFormValue(edits[key], values[key])),
    [edits, values],
  )

  const patch = useMemo(() => {
    const next: Record<string, unknown> = {}
    for (const key of dirtyKeys) next[key] = edits[key]
    return next
  }, [dirtyKeys, edits])

  const allErrors = useMemo(() => {
    if (!validate) return {}
    const draft = { ...values, ...edits }
    const raw = validate(draft)
    const kept: Record<string, string> = {}
    for (const [key, message] of Object.entries(raw)) if (message) kept[key] = message
    return kept
  }, [validate, values, edits])

  const errors = useMemo(() => {
    if (tried) return allErrors
    const shown: Record<string, string> = {}
    for (const key of dirtyKeys) if (allErrors[key]) shown[key] = allErrors[key]
    return shown
  }, [allErrors, dirtyKeys, tried])

  const save = useCallback(async () => {
    if (loading || saving) return
    const { values: currentValues, edits: currentEdits } = latest.current
    const draft = { ...currentValues, ...currentEdits }
    const failures = validate ? validate(draft) : {}
    if (Object.values(failures).some(Boolean)) {
      // Blocked: show every message, not only the ones on edited keys, so the
      // phase and the page agree about why nothing was sent.
      setTried(true)
      return
    }
    const dirty = Object.keys(currentEdits).filter((key) => !sameFormValue(currentEdits[key], currentValues[key]))
    const body: Record<string, unknown> = {}
    for (const key of dirty) body[key] = currentEdits[key]
    setSaving(true)
    setOutcome(null)
    try {
      await onSave(body)
      // The persisted values are the truth now; the draft has nothing left to say.
      setEdits({})
      setTried(false)
      setOutcome({ kind: 'saved' })
    } catch (error) {
      // Kept, not discarded: a refusal is not a reason to lose what somebody typed.
      setOutcome({ kind: 'refused', error })
    } finally {
      setSaving(false)
    }
  }, [loading, saving, validate, onSave])

  /* Precedence, most-informative first. `loading` outranks everything because
     nothing else can be true of data that has not been read; `refused` outranks
     `dirty` because the edits it kept are exactly what makes it dirty. */
  const phase: FormTransactionPhase =
    loading ? 'loading'
      : saving ? 'saving'
        : Object.keys(errors).length > 0 ? 'invalid'
          : outcome?.kind === 'refused' ? 'refused'
            : dirtyKeys.length > 0 ? 'dirty'
              : outcome?.kind === 'saved' ? 'saved'
                : 'idle'

  return {
    phase,
    value,
    set,
    dirtyKeys,
    isDirty: dirtyKeys.length > 0,
    patch,
    errors,
    saveError: outcome?.kind === 'refused' ? outcome.error : null,
    save,
    reset,
  }
}

/** Settings is the first renderer of the shared form transaction. Keep its
 * public names as compatibility aliases; there is still only one lifecycle. */
export type SettingsFormPhase = FormTransactionPhase
export type SettingsFormOptions = FormTransactionOptions
export type SettingsFormState = FormTransactionState
export const sameSettingValue = sameFormValue
export const useSettingsForm = useFormTransaction
